/**
 * ArchiveHero — compact top-of-page banner for /archive. Mirrors the
 * homepage design language (red rule + service tag + font-display headline
 * + operational counters). Sets the tone before the toolbar.
 *
 * Environmental atmosphere: EnvironmentalAtmosphere ("vault" variant)
 * + EnvironmentalSeal ("archive" intensity, 45 %) — the permanent
 * intelligence-facility background system.
 */
import React from "react";
import { ShieldCheck, FileText, Link2 } from "lucide-react";
import { EnvironmentalSeal } from "../EnvironmentalSeal";
import { EnvironmentalAtmosphere } from "../EnvironmentalAtmosphere";

const ArchiveHero = ({ totalCount = 0, sourcesCount = 0 }) => (
  <section
    data-testid="archive-hero"
    className="relative overflow-hidden border-b"
    style={{ borderColor: "#2D2A26", background: "#0B0A0D" }}
  >
    {/* Environmental atmosphere — classified archive-room ambience,
        CSS-only, ~3–5 % composite prominence. */}
    <EnvironmentalAtmosphere variant="vault" />
    {/* Environmental backdrop — looking down onto a classified archive
        table in a dimly lit intelligence room. ~6–8 % visibility with
        vignette + black gradients; felt rather than noticed. */}
    <div
      aria-hidden="true"
      className="absolute inset-0 z-0 pointer-events-none"
      style={{
        backgroundImage: "url(/img/archive-hero-table.png)",
        backgroundSize: "cover",
        backgroundPosition: "center 40%",
        opacity: 0.17,
        filter: "grayscale(45%) sepia(12%) brightness(0.95) contrast(0.95)",
      }}
    />
    <div
      aria-hidden="true"
      className="absolute inset-0 z-0 pointer-events-none"
      style={{
        background:
          "radial-gradient(ellipse 120% 90% at 50% 45%, transparent 0%, rgba(11,10,13,0.45) 60%, rgba(11,10,13,0.85) 100%)," +
          "linear-gradient(to bottom, rgba(11,10,13,0.5) 0%, rgba(11,10,13,0.1) 40%, rgba(11,10,13,0.8) 100%)",
      }}
    />
    {/* Locked branding — Archive index runs the seal at 45 % intensity. */}
    <EnvironmentalSeal intensity="archive" />
    {/* Signature top-of-page red spill */}
    <div
      aria-hidden="true"
      className="absolute inset-0 z-0 pointer-events-none"
      style={{
        background:
          "radial-gradient(ellipse 90% 60% at 50% 0%, rgba(217,45,32,0.14) 0%, transparent 55%)",
      }}
    />

    <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-16 pb-10 md:pt-20 md:pb-14">
      <div className="flex items-center gap-3 mb-6">
        <span className="h-px w-10" style={{ background: "#D92D20" }} />
        <ShieldCheck size={14} className="text-[#D92D20]" />
        <span className="font-mono text-[10px] uppercase tracking-[0.5em] text-[#A39E93]">
          Central Archive · Directorate of Investigations
        </span>
      </div>

      <div className="flex items-end justify-between flex-wrap gap-6">
        <div>
          <h1 className="font-display text-[#E4DFD3] leading-[0.98] tracking-tight
                         text-[40px] sm:text-[52px] md:text-[72px]">
            The Archive.
          </h1>
          <p className="mt-4 max-w-2xl text-[14px] md:text-[15px] leading-relaxed text-[#B9B2A4]">
            Every dossier on file. Every source dated. Every claim traceable.
            Sort by controversy, sift by cabinet, or search the redacted index —
            explore what mainstream archives leave out.
          </p>
        </div>

        <div className="flex items-center gap-6">
          <StatCounter icon={FileText} label="Dossiers" value={totalCount} />
          <StatCounter icon={Link2}    label="Sources"  value={sourcesCount || "—"} />
        </div>
      </div>
    </div>
  </section>
);

const StatCounter = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3">
    <div
      className="w-9 h-9 flex items-center justify-center border"
      style={{ borderColor: "#3A3630", background: "rgba(217,45,32,0.08)" }}
    >
      <Icon size={15} className="text-[#D92D20]" />
    </div>
    <div>
      <div className="font-display text-[24px] md:text-[28px] text-[#E4DFD3] leading-none">
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
      <div className="font-mono text-[9px] uppercase tracking-[0.35em] text-[#8A8377] mt-1">
        {label}
      </div>
    </div>
  </div>
);

export default ArchiveHero;
